import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import Header from 'core/layout/Header';
import MinimalFooter from 'features/feature_home/components/MinimalFooter';
import useJobItem from 'cms/features/feature_job/hooks/UseJobItem';
import {
    Box, Container, Grid, Typography, CircularProgress, Paper,
    Stepper, Step, StepLabel, Button, Stack, Alert, Avatar, Divider, Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { Utils } from 'core/utils';
import SelectCVWidget from '../components/application/SelectCVWidget';
import ApplicationDetailsForm from '../components/application/ApplicationDetailsForm';
import ApplicationJobInfoForm from '../components/application/ApplicationJobInfoForm';
import ApplicationReview from '../components/application/ApplicationReview';
import { HttpService } from 'core/services/http';

const AVATAR_COLORS = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828', '#00838f', '#558b2f', '#6d4c41'];
const nameToColor = (name = '') => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

function JobApplicationPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { data: job, isLoading, isError } = useJobItem(id);
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [applicationSubmitted, setApplicationSubmitted] = useState(false);

    // Check authentication on mount
    useEffect(() => {
        const token = HttpService.getToken();
        const user = HttpService.getUser();
        const isAuthenticated = !!(token && user);
        
        if (!isAuthenticated) {
            // Redirect to login page with redirect URL
            const redirectUrl = encodeURIComponent(`/job/${id}/apply`);
            navigate(`/login?redirect=${redirectUrl}`);
        }
    }, [id, navigate]);

    const token = HttpService.getToken();
    const user = HttpService.getUser();
    const isAuthenticated = !!(token && user);

    const pageShell = (children) => (
        <>
            <Header />
            <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh', pt: { xs: '60px', md: '68px' } }}>
                {children}
            </Box>
            <MinimalFooter />
        </>
    );

    if (!isAuthenticated) {
        return pageShell(
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isLoading) {
        return pageShell(
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError || !job) {
        return pageShell(
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="error">Error loading job details. Please try again.</Alert>
            </Container>
        );
    }

    const handleNextStep = (data) => {
        setFormData({ ...formData, ...data });
        setCurrentStep(currentStep + 1);
    };

    const handleBack = () => setCurrentStep(Math.max(1, currentStep - 1));

    const handleCVSelected = (cv) => {
        setFormData({ ...formData, cv });
    };

    const handleSubmit = async () => {
        if (!job?.id) return;
        if (!formData?.cv?.data?.id) {
            alert('Please select or upload a CV.');
            setCurrentStep(1);
            return;
        }
        try {
            setSubmitting(true);
            const payload = {
                job: job.id,
                // Either cv or cv_file
                cv: formData.cv?.type === 'cv' ? formData.cv.data.id : null,
                cv_file: formData.cv?.type === 'cvFile' ? formData.cv.data.id : null,
                cover_letter: formData.cover_letter || null,
                title: formData.title || null,
                company_title: formData.company_title || null,
            };
            const service = new HttpService();
            // console.log("payload", payload);
            const resp = await service.post('/job/apply/', payload);
            // console.log("resp", resp);
            if (resp?.id) {
                Utils.notifySuccess && Utils.notifySuccess('Application submitted successfully');
                setApplicationSubmitted(true);
            } else {
                Utils.notifyError && Utils.notifyError('Could not submit application');
            }
        } catch (e) {
            console.error(e);
            Utils.notifyError && Utils.notifyError('Submission failed');
        } finally {
            setSubmitting(false);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <Box>
                        <SelectCVWidget onCVSelected={handleCVSelected} />
                        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                            <Button
                                variant="contained"
                                onClick={() => setCurrentStep(2)}
                                disabled={!formData?.cv?.data?.id}
                                size="large"
                            >
                                Next
                            </Button>
                        </Stack>
                    </Box>
                );
            case 2:
                return (
                    <ApplicationDetailsForm
                        initialData={formData}
                        onNext={handleNextStep}
                        onBack={handleBack}
                    />
                );
            case 3:
                return (
                    <ApplicationJobInfoForm
                        initialData={formData}
                        onNext={handleNextStep}
                        onBack={handleBack}
                    />
                );
            case 4:
                return (
                    <ApplicationReview
                        job={job}
                        data={formData}
                        onBack={handleBack}
                        onSubmit={handleSubmit}
                        submitting={submitting}
                    />
                );
            default:
                return null;
        }
    };

    const steps = [
        'Select or Upload CV',
        'Application Details',
        'Job & Company Info',
        'Review & Submit'
    ];

    const companyName = job.company?.title || 'Unknown Company';
    const logoUrl = job.company?.logo || null;
    const salary = job.pay_minimum && job.pay_maximum
        ? `$${job.pay_minimum} – $${job.pay_maximum} / ${job.pay_rate}`
        : null;

    if (applicationSubmitted) {
        return pageShell(
            <Container maxWidth="sm" sx={{ py: 6 }}>
                <Paper
                    elevation={0}
                    sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                >
                    <Box sx={{
                        width: 72, height: 72, borderRadius: '50%',
                        bgcolor: 'success.50', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2,
                    }}>
                        <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main' }} />
                    </Box>
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                        Application Submitted!
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Your application for <strong>{job.title}</strong> at <strong>{companyName}</strong> has been submitted.
                        We'll notify you about the status via email.
                    </Typography>
                    <Stack direction="row" spacing={1.5} justifyContent="center">
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(`/job/${job.id}`)}
                        >
                            Back to Job
                        </Button>
                        <Button variant="contained" disableElevation onClick={() => navigate('/jobs')}>
                            Browse More Jobs
                        </Button>
                    </Stack>
                </Paper>
            </Container>
        );
    }

    return pageShell(
        <Container maxWidth="lg" sx={{ py: 3 }}>
            {/* Back link + title bar */}
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                <Button
                    component={RouterLink}
                    to={`/job/${id}`}
                    startIcon={<ArrowBackIcon />}
                    size="small"
                    color="inherit"
                    sx={{ color: 'text.secondary', textTransform: 'none' }}
                >
                    Back to job
                </Button>
            </Stack>

            <Grid container spacing={3} alignItems="flex-start">
                {/* ── Main: stepper + form ── */}
                <Grid item xs={12} md={8}>
                    {/* Job identity bar */}
                    <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar
                                src={logoUrl}
                                variant="rounded"
                                sx={{
                                    width: 44, height: 44,
                                    bgcolor: logoUrl ? 'grey.100' : nameToColor(companyName),
                                    fontSize: 17, fontWeight: 700,
                                    border: '1px solid', borderColor: 'divider',
                                    flexShrink: 0,
                                }}
                            >
                                {companyName.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle1" fontWeight={600} noWrap>
                                    {job.title}
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {companyName}
                                    </Typography>
                                    {job.jobtypes?.slice(0, 1).map(jt => (
                                        <Chip key={jt.id} label={jt.title} size="small" variant="outlined" sx={{ height: 18, fontSize: 11 }} />
                                    ))}
                                </Stack>
                            </Box>
                        </Stack>
                    </Paper>

                    {/* Stepper + form */}
                    <Paper
                        elevation={0}
                        sx={{ p: { xs: 2.5, sm: 3.5 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}
                    >
                        <Stepper activeStep={currentStep - 1} sx={{ mb: 4 }} alternativeLabel>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        {renderStep()}
                    </Paper>
                </Grid>

                {/* ── Sidebar: job summary ── */}
                <Grid item xs={12} md={4}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            border: '1px solid', borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            position: 'sticky',
                            top: { xs: 72, md: 84 },
                        }}
                    >
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                            You're applying for
                        </Typography>

                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                            <Avatar
                                src={logoUrl}
                                variant="rounded"
                                sx={{
                                    width: 40, height: 40,
                                    bgcolor: logoUrl ? 'grey.100' : nameToColor(companyName),
                                    fontSize: 15, fontWeight: 700,
                                    border: '1px solid', borderColor: 'divider',
                                    flexShrink: 0,
                                }}
                            >
                                {companyName.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600} noWrap>{job.title}</Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>{companyName}</Typography>
                            </Box>
                        </Stack>

                        <Divider sx={{ mb: 2 }} />

                        <Stack spacing={1.75}>
                            {[
                                { icon: <WorkOutlineIcon sx={{ fontSize: 14, color: 'text.disabled' }} />, value: job.jobtypes?.map(jt => jt.title).join(', ') || 'N/A' },
                                { icon: <LocationOnOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />, value: job.company?.city || job.address || 'N/A' },
                                ...(salary ? [{ icon: <AttachMoneyIcon sx={{ fontSize: 14, color: 'text.disabled' }} />, value: salary }] : []),
                            ].map((item, i) => (
                                <Stack direction="row" spacing={1} alignItems="flex-start" key={i}>
                                    <Box sx={{ mt: 0.2 }}>{item.icon}</Box>
                                    <Typography variant="body2" color="text.secondary">{item.value}</Typography>
                                </Stack>
                            ))}
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="caption" color="text.secondary">
                            Step {currentStep} of {steps.length}: <strong>{steps[currentStep - 1]}</strong>
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
}

export default JobApplicationPage;